import { ChevronRight, Clock, AlertTriangle, RotateCcw } from "lucide-react";
import type { DerivedLog, MetaDiff } from "./logDerivation";
import {
  CATEGORY_ICON,
  STATUS_BADGE,
  formatRelative,
  formatAbsolute,
  initialsOf,
  isImageUrl,
} from "./logFormatters";
import MetaDiffCard from "./MetaDiffCard";

/** Raw key/value grid shown when a log has no structured diffs. */
const DetailGrid = ({ details }: { details: Record<string, string> }) => (
  <div className="bg-light rounded-3 p-3">
    <div className="row g-2">
      {Object.entries(details).map(([key, value]) => (
        <div key={key} className="col-md-6">
          <div className="d-flex gap-2 small">
            <span
              className="text-secondary text-capitalize flex-shrink-0"
              style={{ minWidth: 100 }}
            >
              {key.replace(/_/g, " ")}
            </span>
            {isImageUrl(value) ? (
              <img
                src={value}
                alt={key}
                className="rounded border"
                style={{ maxWidth: 120, maxHeight: 68, objectFit: "cover" }}
              />
            ) : (
              <span className="fw-medium" style={{ wordBreak: "break-word" }}>
                {value}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

interface LogEntryProps {
  log: DerivedLog;
  userName: string | null;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  /** Key of the diff/revert-all currently in flight, or null. */
  revertingKey: string | null;
  mediaId?: string;
  onRevert: (diff: MetaDiff) => void;
  onRevertAll: (logId: string, diffs: MetaDiff[]) => void;
}

/** A single timeline entry: dot + collapsible card. */
const LogEntry = ({
  log,
  userName,
  isExpanded,
  onToggle,
  revertingKey,
  mediaId,
  onRevert,
  onRevertAll,
}: LogEntryProps) => {
  const Icon = CATEGORY_ICON[log.category];
  const hasDetails =
    Object.keys(log.details).length > 0 || log.metaDiffs.length > 0;
  const isError = log.status === "error";
  const badge = STATUS_BADGE[log.status];
  const revertableCount = log.metaDiffs.filter((d) => d.hasBefore).length;

  return (
    <div className="d-flex gap-3 align-items-start">
      {/* Dot */}
      <div
        className="position-relative flex-shrink-0 rounded-circle bg-white border d-flex align-items-center justify-content-center shadow-sm"
        style={{ width: 32, height: 32, zIndex: 1 }}
      >
        {isError ? (
          <AlertTriangle size={14} className="text-danger" />
        ) : (
          <Icon size={14} className="text-secondary" />
        )}
      </div>

      {/* Card */}
      <div className="flex-grow-1 card border rounded-3">
        <div
          className={`p-3 ${hasDetails ? "cursor-pointer" : ""}`}
          onClick={hasDetails ? () => onToggle(log.id) : undefined}
          style={hasDetails ? { cursor: "pointer" } : undefined}
        >
          <div className="d-flex align-items-start justify-content-between gap-3">
            <div className="min-w-0 flex-grow-1">
              <div className="d-flex align-items-center gap-2 mb-1">
                <h6 className="fw-semibold mb-0">{log.title}</h6>
                <span
                  className={`badge rounded-pill text-uppercase ${badge.className}`}
                  style={{ fontSize: "0.6rem", letterSpacing: "0.03em" }}
                >
                  {badge.label}
                </span>
              </div>
              {log.description && (
                <p className="text-secondary small mb-2">{log.description}</p>
              )}
              <div className="d-flex align-items-center gap-3 text-secondary small">
                {userName && (
                  <span className="d-flex align-items-center gap-1">
                    <span
                      className="rounded-circle bg-primary-subtle text-primary d-inline-flex align-items-center justify-content-center fw-semibold"
                      style={{ width: 20, height: 20, fontSize: "0.55rem" }}
                    >
                      {initialsOf(userName)}
                    </span>
                    <span className="fw-medium text-body-secondary">
                      {userName}
                    </span>
                  </span>
                )}
                <span className="d-flex align-items-center gap-1">
                  <Clock size={12} />
                  {formatRelative(log.mt)}
                </span>
                <span
                  className="text-body-tertiary"
                  style={{ fontSize: "0.7rem" }}
                >
                  {formatAbsolute(log.mt)}
                </span>
              </div>
            </div>
            {hasDetails && (
              <ChevronRight
                size={16}
                className="text-secondary flex-shrink-0"
                style={{
                  transition: "transform 0.15s",
                  transform: isExpanded ? "rotate(90deg)" : "none",
                }}
              />
            )}
          </div>
        </div>

        {isExpanded && hasDetails && (
          <div className="px-3 pb-3 border-top pt-3">
            {log.metaDiffs.length > 0 && (
              <div className="d-flex flex-column gap-2 mb-3">
                {revertableCount > 1 && (
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <span className="small text-secondary">
                      {revertableCount} fields changed
                    </span>
                    <button
                      className="btn btn-sm btn-primary rounded-pill d-flex align-items-center gap-1"
                      disabled={revertingKey === `${mediaId}-all-${log.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        onRevertAll(log.id, log.metaDiffs);
                      }}
                      title="Revert all changed fields to their previous values"
                    >
                      {revertingKey === `${mediaId}-all-${log.id}` ? (
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                        />
                      ) : (
                        <>
                          <RotateCcw size={12} />
                          <span style={{ fontSize: "11px" }}>Revert all</span>
                        </>
                      )}
                    </button>
                  </div>
                )}
                {log.metaDiffs.map((diff, idx) => (
                  <MetaDiffCard
                    key={idx}
                    diff={diff}
                    reverting={revertingKey === `${mediaId}-${diff.key}`}
                    onRevert={onRevert}
                  />
                ))}
              </div>
            )}
            {log.metaDiffs.length === 0 &&
              Object.keys(log.details).length > 0 && (
                <DetailGrid details={log.details} />
              )}
          </div>
        )}
      </div>
    </div>
  );
};

export default LogEntry;
